const DEFAULT_WS_PATH = "/ws/one-chat/";
function build_default_websocket_url(path) {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  return `${protocol}://${window.location.host}${path}`;
}

function configured_websocket_base_url() {
  return process.env.VUE_APP_ONE_CHAT_WS_URL || process.env.VUE_APP_WEBSOCKET_BASE_URL || "";
}

function websocket_url_from_base(base_url, path) {
  const url = new URL(String(base_url || "").trim());

  if (url.protocol === "https:") {
    url.protocol = "wss:";
  } else if (url.protocol === "http:") {
    url.protocol = "ws:";
  }

  if (!url.pathname || url.pathname === "/") {
    url.pathname = path;
  }

  return url.toString();
}

function api_base_websocket_url(path) {
  const api_base_url = process.env.VUE_APP_API_BASE_URL || "";
  if (!api_base_url) return "";

  const url = new URL(websocket_url_from_base(api_base_url, path));
  url.pathname = path;
  url.search = "";
  url.hash = "";
  return url.toString();
}

function build_websocket_url(options) {
  const path = options.path || DEFAULT_WS_PATH;
  const configured_url = configured_websocket_base_url();

  const base_url = configured_url
    ? websocket_url_from_base(configured_url, path)
    : api_base_websocket_url(path) || build_default_websocket_url(path);

  const url = new URL(base_url);

  if (options.token) {
    url.searchParams.set("token", options.token);
  }

  return url.toString();
}

export default class OneChatWebSocketClient {
  constructor(options = {}) {
    this.options = {
      path: DEFAULT_WS_PATH,
      token: null,
      reconnect_min_delay: 2000,
      reconnect_max_delay: 8000,
      reconnect_jitter: 500,
      heartbeat_interval: 30000,
      ...options,
    };

    this.socket = null;
    this.closed_by_client = false;
    this.reconnect_attempts = 0;
    this.reconnect_timer = null;
    this.heartbeat_timer = null;
    this.request_counter = 0;
    this.pending_requests = new Map();
  }

  connect() {
    if (
      this.socket &&
      [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)
    ) {
      return;
    }

    this.closed_by_client = false;
    this.clear_reconnect_timer();

    this.socket = new WebSocket(build_websocket_url(this.options));
    this.socket.onopen = (event) => this.handle_open(event);
    this.socket.onclose = (event) => this.handle_close(event);
    this.socket.onerror = (event) => this.invoke_callback("on_error", event);
    this.socket.onmessage = (event) => this.handle_message(event);
  }

  disconnect() {
    this.closed_by_client = true;
    this.clear_reconnect_timer();
    this.clear_heartbeat();
    this.reject_pending_requests(new Error("WebSocket disconnected"));

    if (this.socket) {
      this.socket.close(1000, "client_disconnect");
    }
  }

  handle_open(event) {
    this.reconnect_attempts = 0;
    this.start_heartbeat();
    this.invoke_callback("on_open", event);
  }

  handle_close(event) {
    this.clear_heartbeat();
    this.invoke_callback("on_close", event);

    if (!this.closed_by_client) {
      this.schedule_reconnect();
    }
  }

  handle_message(event) {
    const payload = JSON.parse(event.data);
    if (payload.type === "response" && payload.request_id) {
      this.handle_response(payload);
      return;
    }
    this.invoke_callback("on_event", payload);
  }

  invoke_callback(name, payload) {
    let callback = null;
    switch (name) {
      case "on_open":
        callback = this.options.on_open;
        break;
      case "on_close":
        callback = this.options.on_close;
        break;
      case "on_error":
        callback = this.options.on_error;
        break;
      case "on_event":
        callback = this.options.on_event;
        break;
      default:
        break;
    }
    if (typeof callback === "function") {
      callback(payload);
    }
  }

  schedule_reconnect() {
    this.reconnect_attempts += 1;
    const base_delay = Math.min(
      this.options.reconnect_max_delay,
      this.options.reconnect_min_delay * Math.pow(2, this.reconnect_attempts - 1)
    );
    const jitter = Math.floor(Math.random() * this.options.reconnect_jitter);
    const delay = base_delay + jitter;

    this.reconnect_timer = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  clear_reconnect_timer() {
    if (this.reconnect_timer) {
      window.clearTimeout(this.reconnect_timer);
      this.reconnect_timer = null;
    }
  }

  start_heartbeat() {
    this.clear_heartbeat();
    this.heartbeat_timer = window.setInterval(() => {
      this.send({ type: "presence.heartbeat" });
    }, this.options.heartbeat_interval);
  }

  clear_heartbeat() {
    if (this.heartbeat_timer) {
      window.clearInterval(this.heartbeat_timer);
      this.heartbeat_timer = null;
    }
  }

  is_open() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }

  send(payload) {
    if (!this.is_open()) {
      return false;
    }

    this.socket.send(JSON.stringify(payload));
    return true;
  }

  request(type, payload = {}, timeout_ms = 30000) {
    if (!this.is_open()) {
      return Promise.reject(new Error("WebSocket is not connected"));
    }

    const request_id = `${Date.now()}-${++this.request_counter}`;
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.pending_requests.delete(request_id);
        reject(new Error(`WebSocket request timed out: ${type}`));
      }, timeout_ms);

      this.pending_requests.set(request_id, { resolve, reject, timeout });
      this.send({
        type,
        request_id,
        ...payload,
      });
    });
  }

  handle_response(payload) {
    const pending = this.pending_requests.get(payload.request_id);
    if (!pending) return;
    window.clearTimeout(pending.timeout);
    this.pending_requests.delete(payload.request_id);

    if (payload.status >= 200 && payload.status < 300) {
      pending.resolve(payload.data);
      return;
    }

    const error = new Error(payload.data?.message || "WebSocket request failed");
    error.status = payload.status;
    error.responseJSON = payload.data;
    pending.reject(error);
  }

  reject_pending_requests(error) {
    this.pending_requests.forEach(pending => {
      window.clearTimeout(pending.timeout);
      pending.reject(error);
    });
    this.pending_requests.clear();
  }

  subscribe(conversation_id) {
    return this.send({
      type: "conversation.subscribe",
      conversation_id,
    });
  }

  send_message(conversation_id, content, extra = {}) {
    return this.send({
      type: "message.send",
      conversation_id,
      content: content || "",
      ...extra,
    });
  }

  mark_delivered(conversation_id, message_ids) {
    return this.send({
      type: "message.delivered",
      conversation_id,
      message_ids,
    });
  }

  mark_read(conversation_id, last_message_id) {
    return this.send({
      type: "message.read",
      conversation_id,
      last_message_id,
    });
  }

  start_typing(conversation_id) {
    return this.send({
      type: "typing.start",
      conversation_id,
    });
  }

  stop_typing(conversation_id) {
    return this.send({
      type: "typing.stop",
      conversation_id,
    });
  }
}

export { build_websocket_url };
